import { getClassificationLabel } from './getClassificationLabel.js'

export const SITE_NAME = 'Anime Architecture Archive'
export const SITE_URL = 'https://animearchive.app'
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-fallback.png`

function truncate(text, limit = 160) {
  if (!text) return ''
  if (text.length <= limit) return text
  return `${text.slice(0, limit - 1).trimEnd()}…`
}

function buildUniverseDescription(preview) {
  const lens = getClassificationLabel(preview.visualizationHint)
  const stats = preview.stats || {}
  const statsSummary = [
    stats.characters ? `${stats.characters} entities` : null,
    stats.powerSystem ? `${stats.powerSystem} power mechanics` : null,
    stats.rules ? `${stats.rules} governing rules` : null,
  ].filter(Boolean).join(' • ')

  const intro = preview.introductionSummary ? `${preview.introductionSummary} ` : ''
  const sentence = `${preview.anime}: ${intro}${preview.tagline} Structured ${lens.toLowerCase()} analysis covering factions, causal logic, and strategic constraints${statsSummary ? ` (${statsSummary})` : ''}.`
  return truncate(sentence)
}

export function buildHomeSeo(catalog = []) {
  const count = catalog.length || 30
  const description = truncate(
    `Anime Architecture Archive maps ${count} anime universes as engineered systems — power hierarchies, combat rules, faction logic, and causal structure. Not fan summaries. Structural analyses of how each world actually works.`
  )

  return {
    title: `Anime Power Systems Analysis Archive | Compare Anime Worlds`,
    description,
    keywords: 'anime power systems, anime analysis, anime comparison, best anime systems, anime worldbuilding',
    canonicalUrl: `${SITE_URL}/`,
    image: DEFAULT_OG_IMAGE,
    type: 'website',
  }
}


export function buildCatalogSeo(catalog = [], options = {}) {
  const count = catalog.length
  const { activeCluster = '', sortMode = '' } = options || {}
  let description = `Browse ${count} anime universes in a searchable, sortable archive catalog built for power system comparison, faction analysis, and worldbuilding study.`
  if (activeCluster) description += ` Cluster: ${activeCluster}.`
  if (sortMode) description += ` Sorted by ${sortMode}.`
  const title = activeCluster ? `${activeCluster.replace(/-/g, ' ')} Universes | ${SITE_NAME}` : `Browse ${count} Anime Universe Analyses | ${SITE_NAME}`
  return {
    title,
    description: truncate(description),
    canonicalUrl: `${SITE_URL}/universes${activeCluster ? `?cluster=${activeCluster}` : ''}`,
    image: DEFAULT_OG_IMAGE,
    type: 'website',
  }
}

export function buildCatalogStructuredData(catalog = []) {
  const schemas = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${SITE_NAME} Universe Catalog`,
      description: 'Searchable and sortable catalog for anime universe analysis.',
      url: `${SITE_URL}/universes`,
      isPartOf: {
        '@type': 'WebSite',
        name: SITE_NAME,
        url: `${SITE_URL}/`,
      },
      numberOfItems: catalog.length,
      hasPart: catalog.map((entry) => ({
        '@type': 'CreativeWork',
        name: `${entry.anime} System Analysis`,
        url: `${SITE_URL}/universe/${entry.id}`,
        description: entry.tagline || '',
      })),
    },
  ]

  // Add per-universe ItemPage schemas for better SERP presence
  catalog.forEach((entry) => {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'ItemPage',
      name: `${entry.anime} | ${SITE_NAME}`,
      description: entry.tagline || '',
      url: `${SITE_URL}/universe/${entry.id}`,
      isPartOf: {
        '@type': 'CollectionPage',
        name: `${SITE_NAME} Universe Catalog`,
        url: `${SITE_URL}/universes`,
      },
      about: {
        '@type': 'Thing',
        name: entry.anime,
        description: entry.tagline || '',
      },
    })
  })

  return schemas
}

export function buildUniverseSeo(preview) {
  if (!preview) {
    return {
      title: `Anime Universe Analysis | ${SITE_NAME}`,
      description: truncate(
        `Explore structured analyses of 30 anime universes — power systems, character hierarchies, faction networks, and causal logic mapped as systems. Compare any two worlds side-by-side.`
      ),
      canonicalUrl: `${SITE_URL}/universes`,
      image: DEFAULT_OG_IMAGE,
      type: 'website',
    }
  }

  return {
    title: `${preview.anime} System Analysis | ${SITE_NAME}`,
    description: buildUniverseDescription(preview),
    canonicalUrl: `${SITE_URL}/universe/${preview.id}`,
    image: `${SITE_URL}/api/og?id=${preview.id}`,
    type: 'article',
  }
}

export function buildHomeStructuredData(catalog = [], options = {}) {
  const featuredUniverses = options.featuredUniverses || []
  const structureGroups = options.structureGroups || []

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: SITE_NAME,
      url: `${SITE_URL}/`,
      description: 'A curated anime archive and reference graph for fictional universe system analysis.',
      inLanguage: 'en',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${SITE_NAME} Universe Index`,
      url: `${SITE_URL}/`,
      isPartOf: {
        '@type': 'WebSite',
        name: SITE_NAME,
        url: `${SITE_URL}/`,
      },
      hasPart: catalog.map((entry) => ({
        '@type': 'CreativeWork',
        name: `${entry.anime} System Analysis`,
        url: `${SITE_URL}/universe/${entry.id}`,
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Top Featured Archive Systems',
      itemListOrder: 'https://schema.org/ItemListOrderAscending',
      numberOfItems: featuredUniverses.length,
      itemListElement: featuredUniverses.map((entry, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `${SITE_URL}/universe/${entry.id}`,
        name: entry.anime,
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Explore by System Structure',
      itemListElement: structureGroups.map((group, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: `${group.label} (${group.count})`,
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Hashi.Ai',
      url: `${SITE_URL}/`,
      description: 'AI-native anime architecture platform. Structured analysis of fictional universes as systems.',
      sameAs: [
        'https://www.tiktok.com/@hashi.ai',
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Dataset',
      name: SITE_NAME,
      description: 'Machine-readable universe analyses built around characters, rules, factions, and causality.',
      url: `${SITE_URL}/`,
      creator: {
        '@type': 'Organization',
        name: 'Hashi.Ai',
      },
      license: 'https://creativecommons.org/licenses/by-nc/4.0/',
      isAccessibleForFree: true,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is Anime Architecture Archive?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Anime Architecture Archive is a free online reference for anime fans who want to understand how power systems, character hierarchies, and world rules actually work in their favorite shows. It maps 30 anime universes as structural systems — not summaries, but architectural analyses of how each world functions.',
          },
        },
        {
          '@type': 'Question',
          name: 'How is this different from a wiki or anime database?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Most anime sites tell you what happens. We explain why it works structurally — what rules govern the world, how power hierarchies form, what makes fights meaningful, and how causality flows. It is system design thinking applied to fictional universes.',
          },
        },
        {
          '@type': 'Question',
          name: 'Is Anime Architecture Archive free to use?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, entirely free. All universe analyses, character mappings, relationship graphs, and comparison tools are open access. There is no paywall or account required.',
          },
        },
        {
          '@type': 'Question',
          name: 'How does the anime comparison tool work?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Pick any two universes from the compare page and see them side-by-side across system type, power mechanics, factions, combat style, and strategic depth. The comparison is structural, not just "who would win" — it maps how each world creates meaningful conflict.',
          },
        },
        {
          '@type': 'Question',
          name: 'What anime universes are covered?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: `Currently covering 30 anime universes including Attack on Titan, Jujutsu Kaisen, One Punch Man, Naruto, Dragon Ball Z, One Piece, Hunter x Hunter, Death Note, Demon Slayer, Solo Leveling, and more. New universes are added regularly.`,
          },
        },
        {
          '@type': 'Question',
          name: 'Who runs Anime Architecture Archive?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Hashi.Ai — an AI-native platform building structural intelligence layers for anime and fictional universes. It is an independent project built and maintained by an anime fan using AI analysis tools.',
          },
        },
      ],
    },
  ]
}

export function buildCharacterSeo(preview, character, charIndex) {
  if (!preview || !character) return buildHomeSeo()

  const description = truncate(
    character.loreBio
      ? `${character.name} — ${character.title || character.rank || ''}: ${character.loreBio}`
      : `${character.name}: ${character.role || 'character'} in ${preview.anime}. Ranked within the universe's power hierarchy, faction structure, and combat logic — analyzed as a system component.`
  )

  return {
    title: `${character.name} — ${preview.anime} | ${SITE_NAME}`,
    description,
    canonicalUrl: `${SITE_URL}/universe/${preview.id}/character/${charIndex}`,
    image: `${SITE_URL}/api/og?id=${preview.id}`,
    type: 'article',
  }
}

export function buildPowerSystemSeo(preview, power, powerIndex) {
  if (!preview || !power) return buildUniverseSeo(preview)

  const description = truncate(
    power.loreDesc
      ? `${power.name} — ${power.subtitle || ''}: ${power.loreDesc}`
      : `${power.name}: a named power mechanic within ${preview.anime}'s system. Analyzed for combat applications, interaction rules, and structural role in the universe's logic.`
  )

  return {
    title: `${power.name} — ${preview.anime} | ${SITE_NAME}`,
    description,
    canonicalUrl: `${SITE_URL}/universe/${preview.id}/power/${powerIndex}`,
    image: `${SITE_URL}/api/og?id=${preview.id}`,
    type: 'article',
  }
}

export function buildFactionSeo(preview, faction, factionIndex) {
  if (!preview || !faction) return buildUniverseSeo(preview)

  const description = truncate(
    faction.loreDesc
      ? `${faction.name} (${faction.role}): ${faction.loreDesc}`
      : `${faction.name}: a ${faction.role || 'faction'} in ${preview.anime}. Structural role within the universe's power hierarchy, key members mapped, and faction-wide rules and economics analyzed.`
  )

  return {
    title: `${faction.name} — ${preview.anime} | ${SITE_NAME}`,
    description,
    canonicalUrl: `${SITE_URL}/universe/${preview.id}/faction/${factionIndex}`,
    image: `${SITE_URL}/api/og?id=${preview.id}`,
    type: 'article',
  }
}

export function buildCharacterStructuredData(preview, character, charIndex) {
  if (!preview || !character) return []

  const pageUrl = `${SITE_URL}/universe/${preview.id}/character/${charIndex}`

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Archive Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: preview.anime, item: `${SITE_URL}/universe/${preview.id}` },
        { '@type': 'ListItem', position: 3, name: character.name, item: pageUrl },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Thing',
      name: character.name,
      description: truncate(character.loreBio || '', 200),
      url: pageUrl,
      isPartOf: {
        '@type': 'CreativeWork',
        name: `${preview.anime} System Architecture Profile`,
        url: `${SITE_URL}/universe/${preview.id}`,
      },
    },
  ]
}

export function buildPowerSystemStructuredData(preview, power, powerIndex) {
  if (!preview || !power) return []

  const pageUrl = `${SITE_URL}/universe/${preview.id}/power/${powerIndex}`

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Archive Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: preview.anime, item: `${SITE_URL}/universe/${preview.id}` },
        { '@type': 'ListItem', position: 3, name: power.name, item: pageUrl },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Thing',
      name: power.name,
      description: truncate(power.loreDesc || '', 200),
      url: pageUrl,
      isPartOf: {
        '@type': 'CreativeWork',
        name: `${preview.anime} System Architecture Profile`,
        url: `${SITE_URL}/universe/${preview.id}`,
      },
    },
  ]
}

export function buildFactionStructuredData(preview, faction, factionIndex) {
  if (!preview || !faction) return []

  const pageUrl = `${SITE_URL}/universe/${preview.id}/faction/${factionIndex}`

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Archive Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: preview.anime, item: `${SITE_URL}/universe/${preview.id}` },
        { '@type': 'ListItem', position: 3, name: faction.name, item: pageUrl },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Thing',
      name: faction.name,
      description: truncate(faction.loreDesc || '', 200),
      url: pageUrl,
      isPartOf: {
        '@type': 'CreativeWork',
        name: `${preview.anime} System Architecture Profile`,
        url: `${SITE_URL}/universe/${preview.id}`,
      },
    },
  ]
}

export function buildCompareStructuredData(left, right, leftId, rightId) {
  if (!left || !right) return []

  const compareUrl = `${SITE_URL}/compare?left=${leftId}&right=${rightId}`
  const leftUrl = `${SITE_URL}/universe/${leftId}`
  const rightUrl = `${SITE_URL}/universe/${rightId}`
  const description = `Side-by-side system comparison of ${left.anime} and ${right.anime} power mechanics, factions, combat rules, and world logic.`

  return [
    // WebApplication schema — identifies this page as a comparison tool
    {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: `${left.anime} vs ${right.anime} — System Comparison`,
      description,
      url: compareUrl,
      applicationCategory: 'ReferenceApplication',
      operatingSystem: 'Any',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
      },
      isPartOf: {
        '@type': 'WebSite',
        name: SITE_NAME,
        url: `${SITE_URL}/`,
      },
    },
    // ItemList — tracks both universes in the comparison
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: `${left.anime} vs ${right.anime} Universe Comparison`,
      description,
      numberOfItems: 2,
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: `${left.anime} System Analysis`,
          url: leftUrl,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: `${right.anime} System Analysis`,
          url: rightUrl,
        },
      ],
    },
    // Review schema for the left universe
    {
      '@context': 'https://schema.org',
      '@type': 'Review',
      name: `${left.anime} System Review`,
      description: left.tagline || `System architecture analysis of ${left.anime}.`,
      reviewRating: {
        '@type': 'Rating',
        ratingValue: '5',
        bestRating: '5',
        worstRating: '1',
        ratingCount: left.stats?.characters || 0,
      },
      itemReviewed: {
        '@type': 'Thing',
        name: left.anime,
        description: left.tagline || '',
        url: leftUrl,
      },
      author: { '@type': 'Organization', name: 'Hashi.Ai' },
      publisher: { '@type': 'Organization', name: SITE_NAME, url: `${SITE_URL}/` },
    },
    // Review schema for the right universe
    {
      '@context': 'https://schema.org',
      '@type': 'Review',
      name: `${right.anime} System Review`,
      description: right.tagline || `System architecture analysis of ${right.anime}.`,
      reviewRating: {
        '@type': 'Rating',
        ratingValue: '5',
        bestRating: '5',
        worstRating: '1',
        ratingCount: right.stats?.characters || 0,
      },
      itemReviewed: {
        '@type': 'Thing',
        name: right.anime,
        description: right.tagline || '',
        url: rightUrl,
      },
      author: { '@type': 'Organization', name: 'Hashi.Ai' },
      publisher: { '@type': 'Organization', name: SITE_NAME, url: `${SITE_URL}/` },
    },
    // BreadcrumbList
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Archive Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Compare', item: `${SITE_URL}/compare` },
        {
          '@type': 'ListItem',
          position: 3,
          name: `${left.anime} vs ${right.anime}`,
          item: compareUrl,
        },
      ],
    },
  ]
}

export function buildBlogPostStructuredData(post, canonicalUrl) {
  if (!post) return []

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.description || '',
      url: canonicalUrl,
      datePublished: post.date,
      dateModified: post.date,
      author: { '@type': 'Organization', name: post.author || 'Archive Intelligence' },
      publisher: {
        '@type': 'Organization',
        name: SITE_NAME,
        url: `${SITE_URL}/`,
        logo: {
          '@type': 'ImageObject',
          url: `${SITE_URL}/favicon.svg`,
        },
      },
      isPartOf: {
        '@type': 'Blog',
        name: `${SITE_NAME} Blog`,
        url: `${SITE_URL}/blog`,
      },
      image: post.coverImage || DEFAULT_OG_IMAGE,
      keywords: post.keywords || post.tags?.join(', ') || '',
      inLanguage: 'en',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Archive Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
        { '@type': 'ListItem', position: 3, name: post.title, item: canonicalUrl },
      ],
    },
  ]
}

export function buildInsightStructuredData(insight, pageUrl) {
  if (!insight) return []

  const description =
    insight.content?.find((b) => b.type === 'thesis')?.text?.slice(0, 160) ||
    insight.title

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'ScholarlyArticle',
      name: insight.title,
      description,
      url: pageUrl,
      about: {
        '@type': 'Thing',
        name: insight.universeAnime || insight.universe,
      },
      genre: 'System Analysis',
      keywords: insight.tags?.join(', ') || '',
      datePublished: insight.date || '2026-01-01',
      dateModified: insight.date || '2026-01-01',
      author: { '@type': 'Organization', name: 'Hashi.Ai' },
      publisher: {
        '@type': 'Organization',
        name: SITE_NAME,
        url: `${SITE_URL}/`,
      },
      isPartOf: {
        '@type': 'Blog',
        name: `${SITE_NAME} Insights`,
        url: `${SITE_URL}/insights`,
      },
      inLanguage: 'en',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Archive Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Insights', item: `${SITE_URL}/insights` },
        { '@type': 'ListItem', position: 3, name: insight.title, item: pageUrl },
      ],
    },
  ]
}

export function buildUniverseStructuredData(preview, options = {}) {
  if (!preview) return []

  const pageUrl = `${SITE_URL}/universe/${preview.id}`
  const description = buildUniverseDescription(preview)
  const systemQuestions = options.systemQuestions || preview.systemQuestions || []
  const aiInsights = preview.aiInsights || []

  // Build FAQPage schema from systemQuestions
  const faqSchema = systemQuestions.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: systemQuestions.slice(0, 8).map((q) => ({
      '@type': 'Question',
      name: q.question || q.q || q.title || 'What is the ' + preview.anime + ' power system?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: q.answer || q.answerText || q.summary || q.lore || '',
      },
    })),
  } : null

  // Build HowTo schema from aiInsights
  const howToSchema = aiInsights.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: `How to understand ${preview.anime}'s power system`,
    description: description,
    step: aiInsights.slice(0, 3).map((insight, i) => ({
      '@type': 'HowToStep',
      name: insight.title || `Step ${i + 1}`,
      text: insight.summary || insight.text || insight.casual || insight.deep || '',
    })),
  } : null

  const schemas = [
    {
      '@context': 'https://schema.org',
      '@type': 'CreativeWork',
      name: `${preview.anime} System Architecture Profile`,
      description,
      url: pageUrl,
      about: preview.anime,
      isPartOf: {
        '@type': 'CreativeWorkSeries',
        name: SITE_NAME,
        url: `${SITE_URL}/`,
      },
      inLanguage: 'en',
      genre: 'Analytical reference',
      license: 'https://creativecommons.org/licenses/by-nc/4.0/',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Dataset',
      name: `${preview.anime} System Dataset`,
      description,
      url: pageUrl,
      isPartOf: `${SITE_URL}/`,
      measurementTechnique: ['lore analysis', 'system analysis', 'causal mapping'],
      creator: {
        '@type': 'Organization',
        name: 'Hashi.Ai',
        url: 'https://animearchive.app',
      },
      license: 'https://creativecommons.org/licenses/by-nc/4.0/',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Archive Home',
          item: `${SITE_URL}/`,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: preview.anime,
          item: pageUrl,
        },
      ],
    },
  ]

  if (faqSchema) schemas.push(faqSchema)
  if (howToSchema) schemas.push(howToSchema)

  return schemas
}
